import React, { createContext, useCallback, useContext, useState, useEffect, useRef } from "react";
import { Device } from '@twilio/voice-sdk';
import caAPI from "../services/caAPI";
import Swal from "sweetalert2";
import { AtendimentoContext } from "./AtendimentoContext";
import { useStopwatch } from 'react-timer-hook';
import { trackPromise } from "react-promise-tracker";
import phoneRing from "../assets/Audio/modern-phone.mp3"
import utilsService from "../services/utils";

export const VoipContext = createContext({});

export function VoipProvider({ children }) {
  const [inCallStatus, setInCallStatus] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [connection, setConnection] = useState(false);
  const { atendimentoAtivo, setAtendimentoAtivo } = useContext(AtendimentoContext);
  const twilioCallClient = useRef(null);
  const twilioRespondeClient = useRef(null)
  const {
    seconds,
    minutes,
    isRunning,
    start,
    pause,
    reset,
  } = useStopwatch({ autoStart: false });

  const registrarClient = async (client) => {
    await client.register();
  }

  const handleCloseModalIncoming = () => {
    Swal.fire({
      titleText: "Ligação Encerrada",
      icon: 'warning',
      showCloseButton: true
    }).then(() => {
      utilsService.fecharAtividade();
    })
  };

  const clienteTwilioRespondeFactory = useCallback((tokenTwilio, opcoes = {}, idAtendimentoRecebido = null) => {
    const device = new Device(tokenTwilio, opcoes);
    device.on('incoming', (call) => {
      call.on('cancel', () => {
        registrarContador();
        handleCloseModalIncoming();
        setInCallStatus(false);
        setConnection(false);
        setIsMuted(false);
      });

      call.on('disconnect', () => {
        registrarContador()
        handleCloseModalIncoming();
        setInCallStatus(false);
        setConnection(false);
        setIsMuted(false);
      });

      call.on('accept', (call) => {
        if (idAtendimentoRecebido) {
          let atendenteId = { atendente: caAPI.getLocalState('idUsuario') }
          trackPromise(
            caAPI.atendimento.atender(atendenteId, idAtendimentoRecebido).then(res => {
              const novoAtendimentoAtivo = Object.assign({}, res.data);
              setAtendimentoAtivo(novoAtendimentoAtivo);
              setInCallStatus(true);
            }).catch(err => {
              Swal.fire({
                titleText: "Erro",
                text: "Houve um problema ao atribuir o atendente  " + err,
                icon: 'error'
              });
            })
          );
        }
      });
      setConnection(call);
      start();
    });

    device.on('unregistered', () => {
      setConnection(null)
      utilsService.fecharAtividade();
    })

    device.on('registered', () => {
      if (idAtendimentoRecebido) {
        caAPI.atendimento
          .chamar(idAtendimentoRecebido)
          .then(
            (res) => console.log("Chamado")
          ).catch(
            (error) => handleLigacaoPerdida()
          );
      }
    })

    device.on('error', (error, call) => {
      handleLigacaoPerdida();
    });

    device.audio.on('deviceChange', () => {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        console.log("Ainda com permissão")
      })
        .catch((error) => {
          if (error.name === 'NotAllowedError') {
            console.error('Permissão negada pelo usuário.');
          } else if (error.name === 'NotFoundError') {
            console.error('Nenhum dispositivo de áudio foi encontrado.');
          } else if (error.name === 'OverconstrainedError') {
            console.error('As restrições solicitadas não puderam ser atendidas.');
          } else {
            console.error('Erro desconhecido:', error);
          }
          handleLigacaoPerdida();
        });
    });

    return device;
  }, [atendimentoAtivo, setAtendimentoAtivo]);

  const handleLigacaoPerdida = () => {
    if (twilioRespondeClient.current && twilioRespondeClient.current.calls.length) {
      const conexao = twilioRespondeClient.current.calls.shift();
      conexao.ignore()
      twilioRespondeClient.current.disconnectAll();
      twilioRespondeClient.current.destroy();
      setConnection(null);
      twilioRespondeClient.current = null;
    }

    if (twilioCallClient.current && twilioCallClient.current.calls.length) {
      const conexao = twilioCallClient.current.calls.shift();
      conexao.ignore()
      twilioCallClient.current.disconnectAll();
      twilioCallClient.current.destroy();
      setConnection(null);
      twilioCallClient.current = null;
    }

    handleCloseModalIncoming()
  }

  const handleLigacaoAtivaEncerrada = () => {
    let handlerDesconectar = ligacaoDesconectada;
    if (twilioCallClient && twilioCallClient.current) {
      if (connection) {
        let statusConexao = connection.status();
        if (statusConexao === 'open' || statusConexao === 'closed') {
          handlerDesconectar = ligacaoDesconectadaComContador;
        }
        connection.disconnect();
      }
      if (twilioCallClient.current.calls.length) {
        const conexao = twilioCallClient.current.calls.shift();
        conexao.ignore()
      }
      twilioCallClient.current.disconnectAll();
      twilioCallClient.current.destroy();
      setConnection(null);
      twilioCallClient.current = null;
      handlerDesconectar();
    }

    Swal.fire({
      titleText: "Ligação Encerrada",
      icon: 'warning'
    });
  }

  const ligacaoDesconectada = () => {
    handleLigacaoAtivaEncerrada();
    setInCallStatus(false);
    setConnection(false);
    setIsMuted(false);
  };

  const ligacaoDesconectadaComContador = () => {
    registrarContador();
    ligacaoDesconectada();
  };

  //Para Ligação Iniciada
  const setupCallClient = async (tokenTwilio, atendimento = null) => {
    try {
      if (!atendimento && atendimentoAtivo) {
        atendimento = Object.assign({}, atendimentoAtivo);
      }

      if (atendimento) {
        if (!atendimentoAtivo) {
          setAtendimentoAtivo(Object.assign({}, atendimento));
        }
        const twilioDevice = new Device(tokenTwilio, getOpcoesClienteTwilio());
        twilioDevice.on('error', function (error) {
          handleLigacaoAtivaEncerrada();
        });
        twilioDevice.on('unregistered', () => {
          setConnection(null)
          handleLigacaoAtivaEncerrada();
        })
        twilioDevice.audio.on('deviceChange', () => {
          navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
            console.log("Ainda com permissão")
          })
            .catch((error) => {
              if (error.name === 'NotAllowedError') {
                console.error('Permissão negada pelo usuário.');
              } else if (error.name === 'NotFoundError') {
                console.error('Nenhum dispositivo de áudio foi encontrado.');
              } else if (error.name === 'OverconstrainedError') {
                console.error('As restrições solicitadas não puderam ser atendidas.');
              } else {
                console.error('Erro desconhecido:', error);
              }
              handleLigacaoAtivaEncerrada();
            });
        });

        const dadosVoip = {
          id_atendimento: atendimento.id,
          id_cliente: caAPI.getLocalState('idCliente'),
          contato_externo: atendimento.contato_destino,
          contato_interno: atendimento.contato_origem,
          direcao: 'enviado'
        };
        twilioCallClient.current = twilioDevice;
        setInCallStatus(true);
        const call = await twilioDevice.connect({ params: dadosVoip });
        call.on('cancel', () => {
          handleLigacaoAtivaEncerrada()
        });

        call.on('error', (error) => {
          handleLigacaoAtivaEncerrada();
        });

        call.on('reject', (error) => {
          handleLigacaoAtivaEncerrada();
        });

        call.on('ignore', (error) => {
          handleLigacaoAtivaEncerrada();
        });

        call.on('disconnect', () => {
          ligacaoDesconectadaComContador();
        });

        call.on('accept', (call) => {
          caAPI.atendimento.voip.novoIniciarChamada(call.customParameters.get('id_atendimento'), call.parameters.CallSid).then(res => {
            const novoAtendimentoAtivo = Object.assign({}, atendimentoAtivo);
            if (!(novoAtendimentoAtivo.conteudos)) {
              novoAtendimentoAtivo.conteudos = [];
            }
            novoAtendimentoAtivo.conteudos.push(res.data);
            setAtendimentoAtivo(novoAtendimentoAtivo);
          }).catch(err => {
            console.error(err)
          })
        })
        setConnection(call);
        start();
      } else {
        handleLigacaoAtivaEncerrada();
      }
    } catch (e) {
      handleLigacaoAtivaEncerrada();
    }
  }

  // Para Ligação Recebida
  const receiveCall = useCallback(async (connection_socket, idAtendimento) => {
    if (!twilioRespondeClient.current) {
      try {
        let opcoes = getOpcoesClienteTwilio();
        const twilioDevice = clienteTwilioRespondeFactory(connection_socket, opcoes, idAtendimento);
        twilioRespondeClient.current = twilioDevice;
        registrarClient(twilioRespondeClient.current)
      } catch (error) {
        handleLigacaoPerdida();
      }
    }
  }, [clienteTwilioRespondeFactory, twilioRespondeClient]);

  const getOpcoesClienteTwilio = () => {
    return {
      codecPreferences: ["opus", "pcmu"],
      logLevel: 5,
      appName: "Microfrontend Conecta",
      enableImprovedSignalingErrorPrecision: false,
      allowIncomingWhileBusy: false,
      maxCallSignalingTimeoutMs: 3000,
      sounds: {
        incoming: phoneRing,
        outgoing: phoneRing
      }
    };
  }
  const handleCall = () => {
    caAPI.getTokenCcd().then(res => {
      setupCallClient(res.data)
    }).catch(err => {
      Swal.fire({
        titleText: "Erro",
        text: "Houve um problema ao buscar o token da chamada " + err,
        icon: 'error'
      });
    });
  }

  function hangUp() {
    if (twilioRespondeClient.current) {
      twilioRespondeClient.current.disconnectAll();
    }
    if (twilioCallClient.current) {
      handleLigacaoAtivaEncerrada();
    }
    setInCallStatus(false);
  }

  function handleMuteCall() {
    if (connection) {
      const novoMutar = !isMuted;
      connection.mute(novoMutar)
      setIsMuted(connection.isMuted());
    }
  }

  function handleRejeitar() {
    if (connection) {
      connection.ignore();
    }
    if (twilioRespondeClient.current && twilioRespondeClient.current.calls.length) {
      const conexao = twilioRespondeClient.current.calls.shift();
      conexao.ignore()
      twilioRespondeClient.current.disconnectAll();
    }
    if (twilioCallClient.current && twilioCallClient.current.state == 'registered') {
      twilioCallClient.current.disconnectAll();
    }
  }

  function answerCall() {
    connection.accept();
    setInCallStatus(true);
    start();
  }

  const registrarContador = () => {
    pause();
    if (atendimentoAtivo) {
      caAPI.atendimento.getMessages(atendimentoAtivo.id, 1).then((res) => {
        if (res.data && res.data.conteudos) {
          const novoAtendimento = Object.assign({}, atendimentoAtivo);
          novoAtendimento.conteudos = res.data.conteudos
          setAtendimentoAtivo(novoAtendimento);
        }
      }).catch(error => console.log(error));
    }
    reset(null, false);

  };

  return (
    <VoipContext.Provider value={{
      receiveCall,
      inCallStatus,
      handleCall,
      hangUp,
      handleMuteCall,
      isMuted,
      setIsMuted,
      answerCall,
      handleRejeitar,
      connection,
      minutes,
      seconds,
      handleLigacaoPerdida,
      setupCallClient,
      handleLigacaoAtivaEncerrada
    }}>
      {children}
    </VoipContext.Provider>
  )
}
